
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { useContactModal } from "@/hooks/useContactModal"
import { ContactModal } from "@/components/ContactModal"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-all duration-300",
  {
    variants: {
      variant: {
        default: "bg-edgefi-purple text-white hover:bg-edgefi-purple/90 shadow-md hover:shadow-lg",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border-2 border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-edgefi-purple",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-edgefi-purple underline-offset-4 hover:underline font-medium",
        cta: "bg-gradient-to-r from-edgefi-purple to-edgefi-amethyst text-white hover:from-edgefi-purple/90 hover:to-edgefi-amethyst/90 shadow-lg hover:shadow-xl hover:-translate-y-1",
        subtle: "bg-edgefi-purple/10 text-edgefi-purple hover:bg-edgefi-purple/20",
        "phone-sales": "bg-gradient-to-r from-edgefi-purple to-edgefi-amethyst text-white border-0 shadow-lg hover:shadow-xl hover:shadow-edgefi-purple/30",
        "phone-support": "bg-white border-2 border-edgefi-blue text-edgefi-blue hover:bg-edgefi-blue hover:text-white shadow-md hover:shadow-lg",
        "navbar-phone": "bg-transparent text-edgefi-purple border border-edgefi-purple/20 hover:border-2 hover:border-edgefi-purple hover:text-edgefi-purple",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-9 rounded-full px-4",
        lg: "h-12 rounded-full px-8 text-base",
        xl: "h-14 rounded-full px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export interface ContactButtonProps extends Omit<ButtonProps, "onClick"> {
  modalTitle?: string;
  modalDescription?: string;
  modalFormId?: string;
  additionalFields?: Record<string, any>;
  onFormSubmitted?: () => void; // Add the onFormSubmitted prop
}

export const ContactButton = React.forwardRef<HTMLButtonElement, ContactButtonProps>(
  ({ modalTitle, modalDescription, modalFormId, additionalFields = {}, onFormSubmitted, children, ...props }, ref) => {
    const { isOpen, openModal, closeModal } = useContactModal();

    return (
      <>
        <Button onClick={openModal} ref={ref} {...props}>
          {children}
        </Button>
        <ContactModal
          isOpen={isOpen}
          onClose={closeModal}
          title={modalTitle}
          description={modalDescription}
          formId={modalFormId}
          additionalFields={additionalFields}
          onFormSubmitted={onFormSubmitted} // Pass the prop to ContactModal
        />
      </>
    );
  }
);
ContactButton.displayName = "ContactButton";

export { Button, buttonVariants }
